import { findLast, last } from 'lodash';

import { Foreclosure, ForeclosureStatus } from '@willow/graphql-iso/src/app';
import { parseGqlIsoDate, parseGqlIsoDateTime } from '@willow/types-iso';

/**
 * Returns the most recent foreclosure that's still actively driving loan
 * behavior (active or paused). Status-first with endDate fallback for legacy
 * records that pre-date the new `status` field.
 */
export const getActiveForeclosure = (foreclosures?: Foreclosure[] | null): Foreclosure | undefined => {
  if (foreclosures == null) return undefined;
  return last(
    foreclosures.filter((fc) =>
      fc.status
        ? fc.status === ForeclosureStatus.Active || fc.status === ForeclosureStatus.OnHold
        : !fc.endDate && !fc.archivedAt,
    ),
  );
};

export const isForeclosureInactive = (foreclosure: Foreclosure): boolean =>
  foreclosure.status
    ? foreclosure.status === ForeclosureStatus.Completed || foreclosure.status === ForeclosureStatus.Cancelled
    : Boolean(foreclosure.endDate) && !foreclosure.archivedAt;

/**
 * Returns completed or cancelled foreclosures
 */
export const getInactiveForeclosures = (foreclosures?: Foreclosure[] | null): Foreclosure[] => {
  if (foreclosures == null) return [];
  return foreclosures.filter(isForeclosureInactive);
};

export const getForeclosureClosedAt = (foreclosure: Foreclosure): Date => {
  const closedEntry = findLast(
    foreclosure.statusHistory,
    (entry) => entry.status === ForeclosureStatus.Completed || entry.status === ForeclosureStatus.Cancelled,
  );
  if (closedEntry) return parseGqlIsoDateTime(closedEntry.changedAt);
  return parseGqlIsoDate(foreclosure.endDate) ?? parseGqlIsoDateTime(foreclosure.updatedAt);
};
